<template>
  <Modal name="attackSpell">
    <template v-slot:default="{ options }">
      <ModalCard>
        <template v-slot:title>Attack With {{ options.item.name }}</template>
        
        <div class="spellSaveField field has-addons has-addons-centered">
          <div v-if="options.item.saves" class="control">
            <DiceRoll ref="diceRoller" :roll="saveRoll" :pre-roll="true" :character="character" :computed="computed" />
            <div style="transform:translateY(-1rem)">versus <strong>{{ options.item.saves.join(' or ') }}</strong></div>
          </div>
          <div v-else class="control">
            <DiceRoll ref="diceRoller" :roll="roll" :character="character" :computed="computed" />
          </div>
        </div>

        <div v-if="options.item.level > 0" class="field is-grouped is-grouped-centered">
          <div v-if="options.item.extraDamage" class="control">
            <div class="buttons has-addons">
              <a 
                v-for="i in levels" :key="i" 
                :class="{ 'button': true }" 
                :disabled="true"
              >
                {{ i }}
              </a>
            </div>
          </div>
          <div v-if="!options.item.saves && !appState.hardRolls" class="field has-addons has-addons-centered">
            <div class="control">
              <a class="button is-primary" @click="rollDice">Roll</a>
            </div>
            <div v-if="bonuses.inspiration" class="control">
              <a :class="{ 'button': true, 'is-primary': useInspiration }" :disabled="options.removeInspiration" @click="useInspiration = !useInspiration">+1</a>
            </div>
          </div>
          <div v-else-if="bonuses.inspiration" class="field has-addons has-addons-centered">
            <div class="control">
              <a :class="{ 'button': true, 'is-primary': useInspiration }" :disabled="options.removeInspiration" @click="useInspiration = !useInspiration">+1</a>
            </div>
          </div>
        </div>
        <!-- Cantrips -->
        <div v-else class="field is-grouped is-grouped-centered">
          <div class="field has-addons has-addons-centered">
            <div class="control" v-if="!appState.hardRolls">
              <a class="button is-primary" @click="rollDice">Roll</a>
            </div>
            <div v-if="bonuses.inspiration" class="control">
              <a :class="{ 'button': true, 'is-primary': useInspiration }" :disabled="options.removeInspiration" @click="useInspiration = !useInspiration">+1</a>
            </div>
          </div>
        </div>

        <template v-slot:buttons>
          <div class="button is-primary" @click="terminateModal(null, options)" :disabled="(!appState.hardRolls && options.item.damage && !outcomeValue && !options.item.saves) ? true : null">
            <template v-if="options.item.roll?.type === 'health'">
              Roll for Health
            </template>
            <template v-else-if="options.item.roll">
              Roll for Result
            </template>
            <template v-else-if="options.item.damage">
              Roll for Damage
            </template>
            <template v-else>
              Finish Casting Spell
            </template>
          </div>
        </template>
      </ModalCard>
    </template>
  </Modal>
</template>

<script>
import { defineComponent, ref, watch, inject } from 'vue'

import useModal from '@/hooks/useModal'

import Modal from '@/components/Modal'
import ModalCard from '@/components/ModalCard'
import DiceRoll from '@/components/DiceRoll'

export default defineComponent({
  components: {
    DiceRoll,
    Modal,
    ModalCard
  },

  setup (props, { emit }) {
    const character = inject('character')
    const computed = inject('computedCharacter')
    const appState = inject('appState')

    const { currentModal } = useModal()

    const levels = ref([])
    const useInspiration = ref(false)
    watch(currentModal, (modal) => {
      if (modal === null || modal.name !== 'attackSpell') {
        useInspiration.value = false
        return
      }

      let arr = []
      arr.length = computed.characterClass.value.spellLevel
      arr.fill()
      levels.value = arr.map((_, i) => i + 1)
    })

    const saveRoll = ref(null)
    const makeSaveRoll = (modal, inspired) => {
      if (modal === null || modal.name !== 'attackSpell') {
        useInspiration.value = false
        return
      }

      const rollParts = [ '+8', `+${computed.characterClass.spellAbility}`, '+prof' ]
      if (inspired) rollParts.push('+1')
      saveRoll.value = rollParts

      if (inspired && computed.bonuses.value.inspiration) {
        currentModal.value.options.onClose = () => {
          emit('removeInspiration')
        }
      } else {
        delete currentModal.value.options.onClose
      }
    }
    watch(currentModal, (modal) => makeSaveRoll(modal, useInspiration.value))
    watch(useInspiration, (inspired) => makeSaveRoll(currentModal.value, inspired))

    const roll = ref(null)
    const makeRoll = (modal, inspired) => {
      if (modal === null || modal.name !== 'attackSpell') {
        return roll.value = []
      }

      const parts = [ `+1d20`, `+${computed.characterClass.value.spellAbility}`, `+prof` ]

      const bonus = computed.bonuses.value.spellAttack
      if (bonus > 0) parts.push(`+${bonus}`)
      else if (bonus < 0) parts.push(`-${bonus}`)

      if (inspired && computed.bonuses.value.inspiration) {
        parts.push('+1')
      }

      roll.value = parts
    }
    watch(currentModal, (modal) => makeRoll(modal, useInspiration.value))
    watch(useInspiration, (inspired) => makeRoll(currentModal.value, inspired))

    const terminateModal = inject('terminateModal')

    const outcomeValue = ref(null)
    const diceRoller = ref(null)
    const rollDice = () => {
      if (useInspiration.value && computed.bonuses.value.inspiration) {
        currentModal.value.options.onClose = () => {
          emit('removeInspiration')
        }
      } else {
        delete currentModal.value.options.onClose
      }

      if (appState.hardRolls) {
        outcomeValue.value = Math.abs(Number(prompt('What was the outcome of your roll?') || 0))
      } else {
        outcomeValue.value = diceRoller.value.rollDice().outcome
      }
    }
    watch(currentModal, () => {
      outcomeValue.value = null
    })

    return {
      appState,
      character,
      computed,
      ...computed,
      roll,
      saveRoll,
      terminateModal,
      levels,
      diceRoller,
      rollDice,
      outcomeValue,
      useInspiration
    }
  }
})
</script>

<style lang="sass" scoped>
.spellSaveField
  text-align: center
  strong
    text-transform: uppercase
</style>
